import {NoteSelection, MidiNote} from "./note-selection";

/**
 * NoteSource - draws the pitches for a phrase.
 *
 * Stateful draw: uniform over the sounded set minus the previously drawn
 * note, so a note never repeats immediately (with a two-note sounded set
 * this is strict alternation). The exclusion is a filter over the live
 * sounded set on every next() call; the remembered note is never removed
 * from the selection. Filtering and resampling (loop until different)
 * produce the same distribution; filtering wins because it costs one
 * random call per next() and never loops.
 *
 * Two edge cases are deliberate:
 * - Single-note drilling: when only one note sounds, it repeats (the
 *   fallback to the full pool keeps next() total).
 * - Mid-play eviction: the live-read pattern means the remembered last
 *   note can leave the sounded set; filtering it out is then a no-op,
 *   which is exactly right.
 *
 * next() is a total function: it throws on an empty sounded set, which is
 * a programming error - the UI disables play in that state, so next() is
 * never called then.
 *
 * NoteSource knows nothing about rhythm, phrases, gaps, or scheduling.
 */
class NoteSource {

    private readonly selection: NoteSelection;
    private last: MidiNote | null = null;

    constructor(selection: NoteSelection) {
        this.selection = selection;
    }

    /** Uniform random draw from the sounded set minus the previous note. */
    next(): MidiNote {
        const notes = this.selection.soundedNotes();
        if (notes.length === 0) {
            throw new Error('NoteSource.next() called with an empty sounded set');
        }
        const pool = notes.length > 1 ? notes.filter(n => n !== this.last) : notes;
        const drawn = pool[Math.floor(Math.random() * pool.length)];
        this.last = drawn;
        return drawn;
    }
}

export {NoteSource};
